import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useRef } from "react";
import {
  UploadedFile,
  useFileUploaderWithPromise,
} from "../file-upload-context/use-file-uploader";
import {
  updateProjectWithCad,
  UpdateProjectWithCadParams,
} from "./cad-upload-utils";
import {
  updateProjectWithGaussianSplats,
  UpdateProjectWithGaussianSplatsParams,
} from "./gaussian-splats-upload-utils";
import {
  updateAreaWithImgSheet,
  UpdateAreaWithImgSheetParams,
  updateAreaWithImgSheetTiled,
  UpdateAreaWithImgSheetTiledParams,
} from "./image-sheet-utils";
import {
  updateProjectWithPointCloud,
  UpdateProjectWithPointCloudParams,
} from "./point-cloud-upload-utils";
import {
  CommonUpdateProjectParams,
  UploadElementType,
} from "./use-upload-element-types";

/**
 * Callback returned by the useUploadElement hook. Given a file name
 * it will upload the element to the backend and it will add it to the project.
 */
export type UploadElementCallback = (
  args: UploadElementParams,
) => Promise<UploadedFile>;

type UploadElementParams = TypeSpecificUpdateProjectParams &
  Pick<CommonUpdateProjectParams, "areaId" | "name" | "createdAt"> & {
    /** The main file to upload */
    file: File;

    /** Additional files to upload. These uploads have to complete before the element is created. */
    additionalFiles?: File[];

    /**
     * Callback when the file was uploaded, and the element created.
     *
     * @deprecated Use the returned promise instead.
     */
    onFileUploaded?(): void;
  };

/**
 * @returns A callback to upload a file to the backend and add it to the project
 */
export function useUploadElement(): UploadElementCallback {
  const uploadFile = useFileUploaderWithPromise();
  const appStore = useAppStore();
  const dispatch = useAppDispatch();
  const { projectApiClient, coreApiClient } = useApiClientContext();
  const { handleErrorWithDialog } = useErrorHandlers();

  // A promise capturing the current project update
  // as only one update can happen at a time as the first update
  // will have the side effect of creating the parent nodes
  // required by the follow up updates
  const updateProjectPromise = useRef<Promise<void>>(undefined);

  return useCallback<UploadElementCallback>(
    async ({
      file,
      additionalFiles = [],
      onFileUploaded,
      ...updateProjectParams
    }: UploadElementParams) => {
      const elementName =
        updateProjectParams.type === UploadElementType.areaImage ||
        updateProjectParams.type === UploadElementType.areaImageTiled
          ? updateProjectParams.name
          : undefined;

      const additionalUploadsPromise = Promise.all(
        additionalFiles.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              uploadFile({
                file,
                uploadElementType: updateProjectParams.type,
                projectId: projectApiClient.projectId,
                coreApiClient,
                areaId: updateProjectParams.areaId,
                onUploadCompleted: (_, downloadUrl) => resolve(downloadUrl),
                onUploadFailed: reject,
                silent: true,
                elementName,
              });
            }),
        ),
      );

      // eslint-disable-next-line func-style -- FIXME
      const finalizeDownload = async (
        downloadUrl: string,
        md5Hash: string,
      ): Promise<void> => {
        const additionalUrls = await additionalUploadsPromise;

        // Allow only one project update at a time
        // as the first project update on an area will create the parent elements
        // required by the other updates
        if (updateProjectPromise.current) {
          await Promise.allSettled([updateProjectPromise.current]);
        }

        // eslint-disable-next-line require-atomic-updates -- FIXME
        updateProjectPromise.current = updateProject({
          appStore,
          dispatch,
          projectApi: projectApiClient,
          file,
          downloadUrl,
          additionalUrls,
          md5Hash,
          ...updateProjectParams,
        }).catch((error) => {
          // The task store will lose the error instance, so we show a dialog with more information about the failure here.
          handleErrorWithDialog({
            title: `Upload ${updateProjectParams.type}`,
            error,
          });
          throw error;
        });

        return updateProjectPromise.current;
      };

      return await uploadFile({
        file,
        uploadElementType: updateProjectParams.type,
        projectId: projectApiClient.projectId,
        coreApiClient,
        finalizer: finalizeDownload,
        areaId: updateProjectParams.areaId,
        // The file upload should be silent for tiled image sheets as there is already a background task
        // that is created for the tiled image sheet generation and that is showing the progress
        silent: updateProjectParams.type === UploadElementType.areaImageTiled,
        elementName,
        onUploadCompleted: onFileUploaded,
      });
    },
    [
      uploadFile,
      projectApiClient,
      coreApiClient,
      appStore,
      dispatch,
      handleErrorWithDialog,
    ],
  );
}

type UpdateNone = {
  type: UploadElementType.none;
};

type TypeSpecificUpdateProjectParams =
  | UpdateNone
  | UpdateProjectWithCadParams
  | UpdateProjectWithGaussianSplatsParams
  | UpdateProjectWithPointCloudParams
  | UpdateAreaWithImgSheetParams
  | UpdateAreaWithImgSheetTiledParams;

type UpdateProjectParams = CommonUpdateProjectParams &
  TypeSpecificUpdateProjectParams;

function updateProject(params: UpdateProjectParams): Promise<void> {
  switch (params.type) {
    case UploadElementType.none:
      return Promise.resolve();
    case UploadElementType.cad:
      return updateProjectWithCad(params);
    case UploadElementType.gaussianSplats:
      return updateProjectWithGaussianSplats(params);
    case UploadElementType.pointcloud:
      return updateProjectWithPointCloud(params);
    case UploadElementType.areaImage:
      return updateAreaWithImgSheet(params);
    case UploadElementType.areaImageTiled:
      return updateAreaWithImgSheetTiled(params);
  }
}
